import { useRef, useState } from 'react'
import { Plus, X } from 'lucide-react'
import { cn } from '@/utils'
import { normalizeTag, MAX_TAGS } from '@/utils/personaFilters'
import { Badge } from './Badge'
import { Button } from './Button'
import { Input } from './basis/input'

export interface TagInputProps {
  value: string[]
  onChange: (tags: string[]) => void
  suggestions?: string[]
  placeholder?: string
  className?: string
  normalize?: (raw: string) => string
  maxTags?: number
  ariaLabel?: string
}

const MAX_SHOWN = 6

/** The suggestions that contain the typed text, ignoring case, the ones that start with it first (SA-1409). */
function matchSuggestions(
  suggestions: string[],
  draft: string,
  value: string[],
  normalize: (raw: string) => string,
): { shown: string[]; more: number } {
  const q = draft.trim().toLowerCase()
  if (!q) return { shown: [], more: 0 }
  const starts: string[] = []
  const contains: string[] = []
  for (const s of new Set(suggestions)) {
    if (!s.trim() || value.includes(normalize(s))) continue
    const lower = s.toLowerCase()
    if (lower.startsWith(q)) starts.push(s)
    else if (lower.includes(q)) contains.push(s)
  }
  const all = [...starts, ...contains]
  return { shown: all.slice(0, MAX_SHOWN), more: Math.max(0, all.length - MAX_SHOWN) }
}

export function TagInput({
  value,
  onChange,
  suggestions = [],
  placeholder = 'Add a tag and press Enter',
  className,
  normalize = normalizeTag,
  maxTags = MAX_TAGS,
  ariaLabel,
}: TagInputProps) {
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const commit = (raw: string) => {
    const t = normalize(raw)
    if (!t || value.includes(t) || value.length >= maxTags) {
      setDraft('')
      return
    }
    const out: string[] = []
    for (const item of [...value, t]) {
      const n = normalize(item)
      if (n && !out.includes(n)) out.push(n)
    }
    onChange(out.slice(0, maxTags))
    setDraft('')
  }

  const remove = (tag: string) => onChange(value.filter((t) => t !== tag))

  const { shown, more } = value.length < maxTags
    ? matchSuggestions(suggestions, draft, value, normalize)
    : { shown: [], more: 0 }

  return (
    <div
      className={cn('space-y-2', className)}
      // Leaving the whole TagInput adds what was typed; moving within it, or the window losing focus, does not (SA-1409).
      onBlur={(e) => {
        const next = e.relatedTarget
        if (next instanceof Node && e.currentTarget.contains(next)) return
        if (e.currentTarget.contains(document.activeElement)) return
        if (draft !== '') commit(draft)
      }}
      // A press inside never takes focus out of the box, so it never adds the pending text.
      onMouseDown={(e) => {
        if (e.target !== inputRef.current) e.preventDefault()
      }}
    >
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <Badge key={tag} variant="secondary" className="flex items-center gap-1">
              {tag}
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                onClick={(e) => {
                  // A keyboard removal unmounts the focused button; focus goes back to the box, not to the page.
                  const hadFocus = document.activeElement === e.currentTarget
                  remove(tag)
                  if (hadFocus) inputRef.current?.focus()
                }}
                className="hover:text-destructive"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Input
        ref={inputRef}
        type="text"
        value={draft}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            commit(draft)
          } else if (e.key === 'Backspace' && !draft && value.length) {
            remove(value[value.length - 1])
          }
        }}
      />
      {shown.length > 0 && (
        <div role="group" aria-label="Suggestions" className="flex flex-wrap items-center gap-1.5">
          {shown.map((s) => (
            <Button
              key={s}
              type="button"
              size="sm"
              variant="outline"
              leftIcon={<Plus className="h-3 w-3" />}
              aria-label={`Add ${s}`}
              onClick={() => {
                commit(s)
                inputRef.current?.focus()
              }}
            >
              {s}
            </Button>
          ))}
          {more > 0 && <span className="text-xs text-muted-foreground">and {more} more</span>}
        </div>
      )}
    </div>
  )
}
